import { Input } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GLOBAL_DEBOUNCE_TIME } from '../../utils/DataConstants';

interface DebouncedFilterInputProps {
	value?: string;
	onChange?: (value: string | undefined) => void;
	placeholder?: string;
	style?: React.CSSProperties;
	allowClear?: boolean;
	[key: string]: any;
}

export default function DebouncedFilterInput({
	value: externalValue,
	onChange,
	placeholder,
	style,
	allowClear,
}: DebouncedFilterInputProps) {
	const [localValue, setLocalValue] = useState(externalValue || '');
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	// Sync external value only when it meaningfully differs (e.g. filter cleared)
	useEffect(() => {
		setLocalValue(externalValue || '');
	}, [externalValue]);

	const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setLocalValue(val);
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(() => {
			if (onChangeRef.current) {
				onChangeRef.current(val.trim() || undefined);
			}
		}, GLOBAL_DEBOUNCE_TIME);
	}, []);

	useEffect(() => {
		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, []);

	return (
		<Input
			placeholder={placeholder}
			style={style || { width: '100%' }}
			allowClear={allowClear}
			value={localValue}
			onChange={handleChange}
		/>
	);
}
